import { ASSISTANT_COMMERCIAL, COMMERCIAL, DIRECTEUR_COMMERCIAL } from '@/configuration/parametrage/utilisateurConf';
import {
  columnsCommande,
  columnsIntervenantCommande,
  columnsLigneCommande,
} from '@/services/import/commande/templateCommandeGenerale';
import booleanConverter from '@/services/import/General/booleanConverter';
import calcul from '@/services/devisCommandes/calcul/calculCommandeDevis';
import calculArrondi from '@/services/calcul/calculArrondi';
import { REGIE, SOCIETE_ROLE_VENTE } from '@/configuration/societe/societe';
import genererLigneCommande from '@/services/devisCommandes/genererLigneCommande';
import calculLigneCommandeNormal from '@/services/devisCommandes/calcul/calculLigneCommandeNormal';
import i18n from '@/i18n';
import { POURCENTAGE } from '@/configuration/devisConf';
import {
  isNoPropertie,
  isNameExist,
  typeLignes,
  formatPourcentageToNumber,
  findConfrere,
  typeLigneDivers,
} from './services';

function hasNoProduitLigneDivers(ligneCommande) {
  return typeLigneDivers.every(t => isNoPropertie(ligneCommande[`referenceProduit${t}`]));
}

function checkMontantLigne(ligne, key) {
  const errors = [];

  if (!errors.some(e => e.includes('_IS_REQUIRED'))) {
    if (Number.isNaN(ligne[`quantite${key}`]) || Number.isNaN(ligne[`prixUnitaire${key}`]) || Number.isNaN(ligne[`montantBrut${key}`])) {
      errors.push(`${key}_BASIC_CALCUL_INFORMATIONS_NOT_CORRECT`);
    }
    if (Number.isNaN(ligne[`montantBrut${key}`]) || Number.isNaN(ligne[`montantNet${key}`]) || Number.isNaN(formatPourcentageToNumber(ligne[`tauxDeRemise${key}`]))) {
      errors.push(`${key}_NET_CALCUL_INFORMATIONS_NOT_CORRECT`);
    }
  }

  if (errors.length !== 0) return errors;

  const ligneNormal = genererLigneCommande(0);
  ligneNormal.typeRemise = POURCENTAGE;
  ligneNormal.quantite = parseFloat(ligne[`quantite${key}`]);
  ligneNormal.prixUnitaire = parseFloat(ligne[`prixUnitaire${key}`]);
  ligneNormal.totalAvantRemise = parseFloat(ligne[`montantBrut${key}`]);
  ligneNormal.remise = -1 * formatPourcentageToNumber(ligne[`tauxDeRemise${key}`]);
  ligneNormal.totalHtAvecLigneRemise = parseFloat(ligne[`montantNet${key}`]);
  ligneNormal.tauxTva = parseFloat(ligne[`tauxTva${key}`]);
  calculLigneCommandeNormal(ligneNormal);

  if (calculArrondi(Number(ligne[`montantBrut${key}`])) !== calculArrondi(ligneNormal.totalAvantRemise)) {
    errors.push(`${key}_BASIC_CALCUL_NOT_CORRECT`);
  }

  if (calculArrondi(Number(ligne[`montantNet${key}`])) !== calculArrondi(ligneNormal.totalHtAvecLigneRemise)) {
    errors.push(`${key}_NET_CALCUL_NOT_CORRECT`);
  }

  return errors;
}

function checkSubLigne(ligne, key, produits, fournisseurs) {
  let errors = [];
  if (isNoPropertie(ligne[`referenceProduit${key}`])) return errors;

  const produit = produits.find(p => p.reference.toLowerCase() === ligne[`referenceProduit${key}`]);
  if (!produit) errors.push(`${key}_PRODUIT_NOT_FOUND`);
  if (isNoPropertie(ligne[`quantite${key}`])) errors.push(`${key}_${columnsLigneCommande.find(c => c.key === `quantite${key}`)?.label || 'X'}_IS_REQUIRED`);
  if (isNoPropertie(ligne[`unite${key}`])) errors.push(`${key}_${columnsLigneCommande.find(c => c.key === `unite${key}`)?.label || 'X'}_IS_REQUIRED`);
  if (isNoPropertie(ligne[`prixUnitaire${key}`])) errors.push(`${key}_${columnsLigneCommande.find(c => c.key === `prixUnitaire${key}`)?.label || 'X'}_IS_REQUIRED`);
  if (isNoPropertie(ligne[`montantBrut${key}`])) errors.push(`${key}_${columnsLigneCommande.find(c => c.key === `montantBrut${key}`)?.label || 'X'}_IS_REQUIRED`);
  if (isNoPropertie(ligne[`montantNet${key}`])) errors.push(`${key}_${columnsLigneCommande.find(c => c.key === `montantNet${key}`)?.label || 'X'}_IS_REQUIRED`);
  if (isNoPropertie(ligne[`tauxTva${key}`])) errors.push(`${key}_${columnsLigneCommande.find(c => c.key === `tauxTva${key}`)?.label || 'X'}_IS_REQUIRED`);
  if (isNoPropertie(ligne[`tauxDeRemise${key}`])) errors.push(`${key}_${columnsLigneCommande.find(c => c.key === `tauxDeRemise${key}`)?.label || 'X'}_IS_REQUIRED`);
  if (!isNoPropertie(ligne[`fournisseur${key}`])) {
    const confrere = findConfrere(fournisseurs, ligne[`fournisseur${key}`]);
    if (!confrere) {
      errors.push(`${key}_${columnsLigneCommande.find(c => c.key === `fournisseur${key}`)?.label}_NOT_FOUND`);
    }
  }

  // Si tous les champs requis
  if (!errors.some(e => e.includes('_IS_REQUIRED'))) {
    errors = errors.concat(checkMontantLigne(ligne, key));
  }
  if (errors.length) checkMontantLigne(ligne, key);
  return errors;
}

export function validationCommande(commande, societe, users) {
  let errors = [];
  columnsCommande.forEach(column => {
    const columnErrors = column.checkValue(commande[column.key]);
    if (columnErrors.length > 0) {
      errors = errors.concat(columnErrors);
    }

    if (!commande[column.key]) return;
    const value = commande[column.key];
    switch (column.key) {
      case 'commercial':
        if (!users.find(u => [COMMERCIAL, DIRECTEUR_COMMERCIAL].includes(u.profil)
                && isNameExist(value, u.nom, u.prenom))) {
          errors.push('COMMERCIAL_NOT_FOUND');
        }
        break;
      case 'assistant':
        if (!users.find(
          u => u.profil === ASSISTANT_COMMERCIAL && isNameExist(value, u.nom, u.prenom),
        )) errors.push('ASSISTANT_NOT_FOUND');
        break;
      case 'agence':
        if (!societe.agences.find(
          a => a.libelle.toLowerCase() === commande[column.key].toLowerCase(),
        )) errors.push('AGENCE_NOT_FOUND');
        break;
      case 'montantTtc':
        if (+commande.montantHt + +commande.montantTva !== +commande.montantTtc) {
          errors.push('TOTAL_TTC_NOT_CORRECT');
        }
        break;
      case 'role':
        if (!SOCIETE_ROLE_VENTE.find(r => i18n.t(r.libelle).replace('$$', '').toLowerCase() === value.toLowerCase())) {
          errors.push('ROLE_NOT_FOUND');
        }
        break;
      default:
        break;
    }
  });

  const renouvelable = booleanConverter(commande.renouvelable);
  const reconductible = booleanConverter(commande.reconductible);

  if (reconductible && !renouvelable) {
    errors.push('RECONDUCTION_WITH_OUT_RENOUVELABLE');
  }

  if ((reconductible || renouvelable) && isNoPropertie(commande.dateRenouvellement)) {
    errors.push(`${columnsCommande.find(c => c.key === 'dateRenouvellement')?.label || 'X'}_IS_REQUIRED`);
  }

  if ((reconductible || renouvelable) && isNoPropertie(commande.datePreavis)) {
    errors.push(`${columnsCommande.find(c => c.key === 'datePreavis')?.label || 'X'}_IS_REQUIRED`);
  }

  if (!isNoPropertie(commande.DatePreavis) && !renouvelable) {
    errors.push('NO_DATE_PREAVIS_IF_NO_RENOUVELABLE');
  }

  if (!isNoPropertie(commande.dateRenouvellement) && !renouvelable) {
    errors.push('NO_DATE_RENOUVELLEMENT_IF_NO_RENOUVELABLE');
  }

  if (errors.length > 0) return errors;
  return [];
}

export function validationIntervenants(intervenants, tiers, commande) {
  let errors = [];
  if (intervenants.length === 0) errors.push('COMMANDE_HAS_NO_INTERVENANTS');
  if (commande.role) {
    const role = SOCIETE_ROLE_VENTE.find(r => i18n.t(r.libelle).replace('$$', '').toLowerCase() === commande.role.toLowerCase())?.code;
    if (!!role && role !== REGIE && intervenants.filter(
      i => booleanConverter(i.payeur)
      && !Number.isNaN(i.repartition.replace('%', ''))
      && Number(i.repartition.replace('%', '')) !== 0,
    ).length > 1
    ) {
      errors.push('INTERVENANT_TOO_MUCH_FOR_ROLE');
    }
  }

  for (let i = 0; i < intervenants.length; i += 1) {
    const intervenant = intervenants[i];
    let errorsInter = [];
    columnsIntervenantCommande.forEach(column => {
      const columnErrors = column.checkValue(intervenant[column.key]);
      if (columnErrors.length > 0) {
        errorsInter = errorsInter.concat(columnErrors);
      }
    });

    if (intervenant.codeTiers) {
      if (!tiers.find(t => t.code === intervenant.codeTiers)) errorsInter.push('TIERS_NOT_FOUND');
    } else if (intervenant.referenceExterne) {
      if (!tiers.find(t => t.referenceExterne === intervenant.referenceExterne)) errorsInter.push('TIERS_NOT_FOUND');
    } else if (intervenant.nomCommercial) {
      const tiersByName = tiers.filter(
        t => t.nomCommercial.trim().toLowerCase()
        === intervenant.nomCommercial.trim().toLowerCase(),
      );
      if (tiersByName.length > 1) errorsInter.push('TIERS_MULTI_FOUND');
      if (tiersByName.length === 0) errorsInter.push('TIERS_NOT_FOUND');
    }

    if (errorsInter.length > 0) {
      errors = errors.concat(errorsInter.map(e => `INTERVENANT_${intervenant.numLigne}_${e}`));
    }
  }
  const sumQuotePart = intervenants
    .filter(i => booleanConverter(i.payeur))
    .reduce(
      (a, i) => {
        const repartition = i.repartition.replace('%', '');
        if (!Number.isNaN(repartition)) {
          return a + Number(repartition);
        }
        return a;
      }, 0);
  if (sumQuotePart !== 100) errors.push('SUM_QUOTE_PART_NOT_CORRECT');
  if (errors.length > 0) return errors;
  return [];
}

export function validationLigneCommande(lignesCommandes, produits, fournisseurs) {
  let errors = [];
  if (lignesCommandes.length === 0) errors.push('COMMANDE_HAS_NO_LIGNES');

  for (let i = 0; i < lignesCommandes.length; i += 1) {
    const ligneCommande = lignesCommandes[i];
    let errorLigne = [];
    columnsLigneCommande.forEach(column => {
      const columnErrors = column.checkValue(ligneCommande[column.key]);
      if (columnErrors.length > 0) errorLigne = errorLigne.concat(columnErrors);
    });

    let produitRegroupement = null;
    if (isNoPropertie(ligneCommande.produit)) {
      produitRegroupement = produits.find(
        p => p.reference.toLowerCase() === ligneCommande.produitReference?.toLowerCase(),
      );
    } else {
      produitRegroupement = produits.find(
        p => p.reference.toLowerCase() === ligneCommande.produit?.toLowerCase(),
      );
    }
    if (!produitRegroupement && hasNoProduitLigneDivers(ligneCommande)) errors.push('REGROUPEMENT_PRODUIT_NOT_FOUND');

    typeLignes.forEach(type => {
      errorLigne = errorLigne.concat(checkSubLigne(ligneCommande, type, produits, fournisseurs));
    });

    if (errorLigne.length > 0) errors = errors.concat(errorLigne.map(e => `LIGNE_COMMANDE_${ligneCommande.numLigne + 1}_${e}`));
  }
  return errors;
}

export function validationCreationProduit(lignesCommandes, produits, fournisseurs, motifs) {
  let errors = [];
  for (let i = 0; i < lignesCommandes.length; i += 1) {
    const ligneCommande = lignesCommandes[i];
    const errorLigne = [];
    let produit = null;
    if (!isNoPropertie(ligneCommande.produit)) {
      produit = produits.find(
        p => p.reference.toLowerCase() === ligneCommande.produit.toLowerCase(),
      );
    }

    if (!produit && isNoPropertie(ligneCommande.referenceProduitHonoraire)) {
      if (isNoPropertie(ligneCommande.format)) errorLigne.push(`${columnsLigneCommande.find(c => c.key === 'format')?.label || 'X'}_IS_REQUIRED`);
      if (isNoPropertie(ligneCommande.ville)) errorLigne.push(`${columnsLigneCommande.find(c => c.key === 'ville')?.label || 'X'}_IS_REQUIRED`);

      if (!isNoPropertie(ligneCommande.faceMotifNonCom)) {
        const motif = motifs.find(
          m => m.libelle.toLowerCase() === ligneCommande.faceMotifNonCom.toLowerCase(),
        );
        if (!motif) {
          errors.push(`PRODUIT_${columnsLigneCommande.find(c => c.key === 'faceMotifNonCom')?.label}_NOT_FOUND`);
        }
      }

      if (!isNoPropertie(ligneCommande.confrere)) {
        const confrere = findConfrere(fournisseurs, ligneCommande.confrere);
        if (!confrere) {
          errors.push(`PRODUIT_${columnsLigneCommande.find(c => c.key === 'confrere')?.label}_NOT_FOUND`);
        }
      }
    }

    if (errorLigne.length > 0) errors = errors.concat(errorLigne.map(e => `LIGNE_COMMANDE_${ligneCommande.numLigne}_${e}`));
  }
  return errors;
}

export function validationCommandeGeneree(commandeGeneree) {
  const errors = [];

  const tiersIds = commandeGeneree.intervenants.map(i => i.tiersId);
  if ([...new Set(tiersIds)].length !== tiersIds.length) {
    errors.push('COMMANDE_INTERVENANTS_DOUBLE');
  }

  const montantHt = calculArrondi(commandeGeneree.montantHt);
  const montantTva = calculArrondi(commandeGeneree.montantTva);
  const montantTtc = calculArrondi(commandeGeneree.montantTtc);

  calcul(commandeGeneree);

  if (
    calculArrondi(commandeGeneree.montantHt) !== montantHt
    || calculArrondi(commandeGeneree.montantTva) !== montantTva
    || calculArrondi(commandeGeneree.montantTtc) !== montantTtc
  ) {
    errors.push('MONTANT_ERROR');
  }
  return errors;
}

export function validationEcheancier(commande, lignesCommandes) {
  let errors = [];

  for (let i = 0; i < lignesCommandes.length; i += 1) {
    const ligneCommande = lignesCommandes[i];
    const errorLigne = [];

    typeLignes.forEach(type => {
      const montantLigne = ligneCommande[`montantNet${type}`];
      const montantPremiereEcheance = ligneCommande[`montantPremiereEcheance${type}`];
      if (
        !isNoPropertie(montantLigne)
        && !isNoPropertie(montantPremiereEcheance)
        && Number(montantLigne) < Number(montantPremiereEcheance)) {
        errorLigne.push(`LIGNE_COMMANDE_${type}_SMALLER_THAN_FIRST_ECHEANCE`);
      }

      if (!isNoPropertie(ligneCommande[`montantPremiereEcheance${type}`])) {
        if (isNoPropertie(commande.echeancierDateDebut)) errorLigne.push(`${columnsCommande.find(c => c.key === 'echeancierDateDebut')?.label || 'X'}_IS_REQUIRED`);
        if (isNoPropertie(commande.echeancierDateFin)) errorLigne.push(`${columnsCommande.find(c => c.key === 'echeancierDateFin')?.label || 'X'}_IS_REQUIRED`);
        if (isNoPropertie(commande.echeancierDateFinPremiereEcheance)) errorLigne.push(`${columnsCommande.find(c => c.key === 'echeancierDateFinPremiereEcheance')?.label || 'X'}_IS_REQUIRED`);
      }
    });

    if (errorLigne.length > 0) errors = errors.concat(errorLigne.map(e => `ECHEANCIER_${ligneCommande.numLigne}_${e}`));
  }

  return errors;
}
